import type { PublicResearchModel } from '@/lib/research-assistant/models';
import type { ModelMessage } from 'ai';
import {
  DOCUMENT_ACCEPT_ATTRIBUTE,
  documentFileTypeFromName,
} from '@/lib/research-assistant/document-attachments';

type AttachmentModel = Pick<PublicResearchModel, 'label' | 'supportsFiles' | 'supportsImages'> & {
  supportedFileMimeTypes?: readonly string[];
};

export function modelSupportsImageAttachments(model: AttachmentModel | null | undefined): boolean {
  return Boolean(model?.supportsImages);
}

export function imageAttachmentsUnavailableMessage(
  model: AttachmentModel | null | undefined
): string {
  return `Image attachments are not available for ${model?.label || 'the selected model'}.`;
}

export function modelSupportsDocumentAttachments(
  model: AttachmentModel | null | undefined
): boolean {
  return Boolean(model?.supportsFiles);
}

export function documentAttachmentsUnavailableMessage(
  model: AttachmentModel | null | undefined
): string {
  return `Document attachments are not available for ${model?.label || 'the selected model'}.`;
}

export function modelSupportsDocumentFile(
  model: AttachmentModel | null | undefined,
  name: string
): boolean {
  if (!model?.supportsFiles) return false;
  const fileType = documentFileTypeFromName(name);
  if (!fileType) return false;
  // Text formats are decoded and sent as text, rather than native file parts.
  return (
    fileType.kind === 'text' ||
    !model.supportedFileMimeTypes ||
    model.supportedFileMimeTypes.includes(fileType.mimeType)
  );
}

export function modelDocumentAcceptAttribute(model: AttachmentModel | null | undefined): string {
  return DOCUMENT_ACCEPT_ATTRIBUTE.split(',')
    .filter(extension => modelSupportsDocumentFile(model, `document${extension}`))
    .join(',');
}

export function unsupportedDocumentAttachmentMessage(
  model: AttachmentModel | null | undefined,
  documents: readonly { name: string }[]
): string | undefined {
  const unsupported = documents.find(document => !modelSupportsDocumentFile(model, document.name));
  return unsupported
    ? `${unsupported.name} is not supported by ${model?.label || 'the selected model'}. Choose a supported document format.`
    : undefined;
}

export function unsupportedModelFileMessage(
  model: AttachmentModel,
  messages: readonly ModelMessage[]
): string | undefined {
  for (const message of messages) {
    if (!Array.isArray(message.content)) continue;
    for (const part of message.content) {
      if (part.type !== 'file') continue;
      if (
        model.supportsFiles &&
        (!model.supportedFileMimeTypes || model.supportedFileMimeTypes.includes(part.mediaType))
      ) {
        continue;
      }
      return `${part.filename || 'This document'} is not supported by ${model.label}. Choose a supported document format.`;
    }
  }
  return undefined;
}

export function unsupportedDocumentMimeTypesMessage(
  model: AttachmentModel,
  mimeTypes: readonly string[]
): string | undefined {
  const unsupported = mimeTypes.some(
    mimeType =>
      !model.supportsFiles ||
      (model.supportedFileMimeTypes && !model.supportedFileMimeTypes.includes(mimeType))
  );
  return unsupported
    ? `${model.label} does not support a document format already present in this turn. Choose a compatible model.`
    : undefined;
}
